import React, { useEffect } from 'react';
import Layout from '../layouts/Layout';
import { PUBLIC_URL } from '../utils/const';
import { BlogLanguageWrapper, BlogPage } from 'reactyll';
import MarkDown from 'react-markdown'
import rehypeRaw from 'rehype-raw';
import { Head } from 'vite-react-ssg';
import { OG_IMAGE, SITE_ORIGIN, Locale, isLocale, ogLocale, organizationJsonLd } from '../utils/seo';
import { Container, Title, Text, Image, Box, Paper, Anchor, Group, Button, Stack, Divider } from '@mantine/core';
import { redirectWithParameters } from '../utils/common';
import { getBlogByLanguage } from '../utils/blog';
import { blogs } from '../_blog/routes';
import { useTranslation } from 'react-i18next';
import MoreFromOurBlog from '../components/MoreFromOurBlog';
import { trackCtaClick } from '../utils/analytics';

export interface ExtraProperties {
    date: string
    headline: string
    description: string
    publishDate: string
    readMore?: string[]
}

const content: BlogPage<ExtraProperties> = JSON.parse(decodeURIComponent(atob(`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`)));


const InnerBlogTemplate: React.FC = () => {
    const { i18n } = useTranslation();

    const matchingLanguageCode = content.properties.language || i18n.resolvedLanguage;
    const pageLang: Locale = isLocale(content.properties.language) ? content.properties.language : 'en';
    const englishUrl = content.languages.find(([language]) => language === 'en')?.[1] ?? content.properties.url;
    const jsonLd = {
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        headline: content.properties.title,
        description: content.properties.description,
        datePublished: content.properties.publishDate,
        inLanguage: pageLang,
        mainEntityOfPage: `${SITE_ORIGIN}${content.properties.url}`,
        author: organizationJsonLd(),
        publisher: organizationJsonLd(),
        image: OG_IMAGE,
    };    
    
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const langParam = urlParams.get('lang');

        if (langParam) {
            const matchingLanguage = content.languages.find(([lang, url]) => lang === langParam);
            if (matchingLanguage) {
                const [_, url] = matchingLanguage;
                window.location.href = `${url}`;
            }
        }
        

        const handleButtonClick = (event: any) => {
            if (event.target.classList.contains('cta')) {
                trackCtaClick('blog', 'inline');
                window.location.href = "/login?signup=true"
            }
        };

        const parentElement = document.getElementById('blog-wrapper');
        if (parentElement) {
            document.addEventListener('click', handleButtonClick);
        }

        return () => {
            if (parentElement) {
                parentElement.removeEventListener('click', handleButtonClick);
            }
        };
    }, []);

    return (
        <Box bg="gray.0">
            <Head htmlAttributes={{ lang: pageLang }}>
                <meta charSet="utf-8" />
                <title>{content.properties.title}</title>
                <link rel="canonical" href={`${SITE_ORIGIN}${content.properties.url}`} />
                {content.languages.map(([language, url]) => (
                    <link rel="alternate" key={language} href={`${SITE_ORIGIN}${url}`} hrefLang={language} />
                ))}
                <link rel="alternate" href={`${SITE_ORIGIN}${englishUrl}`} hrefLang="x-default" />
                <meta name="description" content={content.properties.description} />
                <meta property="og:site_name" content="Calensync" />
                <meta property="og:type" content="article" />
                <meta property="og:locale" content={ogLocale(pageLang)} />
                <meta property="og:title" content={content.properties.title} />
                <meta property="og:url" content={`${SITE_ORIGIN}${content.properties.url}`} />
                <meta property="og:description" content={content.properties.description} />
                <meta property="og:image" content={OG_IMAGE} />
                <meta property="article:published_time" content={content.properties.publishDate} />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={content.properties.title} />
                <meta name="twitter:description" content={content.properties.description} />
                <meta name="twitter:image" content={OG_IMAGE} />
                <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
            </Head>
            <Container size="md" mt="md" id="blog-wrapper">
                <Paper p="md" my="md" bg="gray.0">
                    <Box mb="md" mt="md">
                        <Title order={1} mb="xs">{content.properties.title}</Title>
                        <Text fs="italic" c="dimmed">{content.properties.date}</Text>
                    </Box>

                    <Box mb="xl">
                        <MarkDown
                            rehypePlugins={[rehypeRaw]}
                            children={content.markdown}
                            components={{
                                h1(props) {
                                    const { children } = props
                                    return <Title order={1}>{children}</Title>
                                },
                                p(props) {
                                    const { children } = props
                                    return <Text size="lg" mb="md">
                                        {children}
                                    </Text>
                                },
                                strong(props) {
                                    const { children } = props
                                    return <Text span fw={700}><b>{children}</b></Text>
                                },
                                h2(props) {
                                    const { children } = props
                                    return <Title fw={400} order={2} mt="xl" pt="md" pb="md">{children}</Title>
                                },
                                h3(props) {
                                    const { children } = props
                                    return <Title fz="xl" fw={300} order={3} mt="md" pt="sm" pb="sm">{children}</Title>
                                },
                                img(props) {
                                    const { src, alt, style, width } = props
                                    return <Image
                                        mx="auto"
                                        my="sm"
                                        src={src}
                                        alt={alt}
                                        style={style}
                                        w={width}
                                        radius="md"
                                    />
                                },
                                a(props) {
                                    const { children, href } = props
                                    if (typeof children === 'string' && children.includes('CUSTOM_BUTTON')) {
                                        const content = children.replace('CUSTOM_BUTTON:', '')
                                        const onClick = () => {
                                            trackCtaClick('blog', 'button');
                                            redirectWithParameters('login', { signup: 'true' }, true)
                                        }
                                        return (
                                            <Group justify='center' my='md'>
                                                <Stack justify='center' my='md' gap="0">
                                                    <Button color="primary.4" size="compact-md" className="cta" onClick={onClick}>{content}</Button>
                                                    <Text size="sm" c="dimmed">.. and get 7 days free trial</Text>
                                                </Stack>
                                            </Group>
                                        )
                                    } else if (href.toLowerCase().startsWith('blog/')) {
                                        const blogId = href.toLowerCase().replace('blog/', '')
                                        const content = getBlogByLanguage(blogs[blogId], matchingLanguageCode)
                                        return <Anchor href={content.url}>{children}</Anchor>
                                    } else {
                                        return <Anchor href={href}>{children}</Anchor>
                                    }
                                }
                            }}
                        />
                    </Box>
                    {content.properties.readMore && (
                        <MoreFromOurBlog 
                            blogs={content.properties.readMore.map((blogId) => blogs[blogId])} 
                            matchingLanguageCode={matchingLanguageCode} 
                        />
                    )}
                </Paper>
            </Container>
        </Box>
    );
};

const BlogTemplate: React.FC = () => {
    return (
        <Layout>
            <InnerBlogTemplate />
        </Layout>
    );
};

export default BlogTemplate;